import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Check, Beef, Egg, Fish, Drumstick, Utensils, ShoppingBasket,
  Apple, Salad, Heart, ArrowRight, Sun, Sandwich, ChefHat, ClipboardList,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const PORTIONS = [
  { key: "protein",    icon: Beef,  label: "PROTEIN",    sub: "Palm size · meat, fish, eggs, tofu, yoghurt" },
  { key: "carbs",      icon: Sandwich, label: "CARBS",   sub: "Cupped hand · rice, oats, potato, bread" },
  { key: "vegetables", icon: Salad, label: "VEGETABLES", sub: "≈ 2 fists · leafy, colourful, cooked or raw" },
];

const HAND_PLATE = [
  { label: "PROTEIN",    portion: "1–2 palms",       body: "Meat, fish, eggs, tofu, yoghurt at main meals." },
  { label: "CARBS",      portion: "1 cupped hand",   body: "Rice, potato, oats, pasta, bread or fruit — adjusted to appetite and activity." },
  { label: "VEGETABLES", portion: "≈ 2 fists",       body: "Aim for two fists at lunch and dinner where practical." },
  { label: "HEALTHY FATS", portion: "1 thumb",       body: "Olive oil, avocado, nuts or seeds — deliberate, not fearful." },
];

const PROTEIN_CARDS = [
  { id: "Beef",    icon: Beef,     to: "/app/eat/meals?protein=Beef",    count: 10 },
  { id: "Eggs",    icon: Egg,      to: "/app/eat/meals?protein=Eggs",    count: 10 },
  { id: "Fish",    icon: Fish,     to: "/app/eat/meals?protein=Fish",    count: 10 },
  { id: "Chicken", icon: Drumstick, to: "/app/eat/meals?protein=Chicken", count: 10 },
];

const QUICK = [
  { id: "Breakfast", icon: Sun,      to: "/app/eat/meals?occasion=Breakfast" },
  { id: "Lunch",     icon: Sandwich, to: "/app/eat/meals?occasion=Lunch" },
  { id: "Dinner",    icon: ChefHat,  to: "/app/eat/meals?occasion=Dinner" },
];

const PROTEIN_COLOR = { Beef: "bg-red-600", Eggs: "bg-amber-500", Fish: "bg-sky-600", Chicken: "bg-orange-500" };

export default function Eat() {
  const [featured, setFeatured] = useState([]);
  const [favCount, setFavCount] = useState(0);
  const [portions, setPortions] = useState({ protein: false, carbs: false, vegetables: false });
  const [savedPortions, setSavedPortions] = useState({ protein: false, carbs: false, vegetables: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [meals, favs, logs] = await Promise.all([
          api.get("/eat/meals?featured=true"),
          api.get("/eat/favourites"),
          api.get("/track/logs"),
        ]);
        setFeatured(meals.data.slice(0, 6));
        setFavCount(favs.data.length);
        const t = logs.data.find((l) => l.date === today());
        const score = Number(t?.nutrition_score || 0);
        const rehydrated = { protein: score >= 1, carbs: score >= 2, vegetables: score >= 3 };
        setPortions(rehydrated);
        setSavedPortions(rehydrated);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, []);

  const total = Object.values(portions).filter(Boolean).length;
  const savedTotal = Object.values(savedPortions).filter(Boolean).length;
  const dirty = JSON.stringify(portions) !== JSON.stringify(savedPortions);

  const toggle = (key) => setPortions((p) => ({ ...p, [key]: !p[key] }));

  const logPlate = async () => {
    setSaving(true);
    try {
      await api.post("/track/log", { date: today(), nutrition_score: total });
      setSavedPortions(portions);
      toast.success(total === 3 ? "Full plate logged. Nice work." : `${total}/3 logged for today.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = () => setPortions({ protein: false, carbs: false, vegetables: false });

  return (
    <div className="space-y-10" data-testid="eat-home">
      {/* Hero */}
      <div className="bg-foreground text-background rounded-lg p-8 md:p-10">
        <div className="label-eyebrow !text-background/70">The Eat Portal</div>
        <h1 className="wordmark mt-3 text-4xl md:text-6xl uppercase leading-[0.95]">
          Eat Real Food. <span className="text-primary">Keep It Simple.</span>
        </h1>
        <p className="mt-4 max-w-xl text-background/80 leading-relaxed">
          Build most meals around protein, produce and a sensible carbohydrate portion. No counting. No fear foods.
        </p>
      </div>

      {/* Hand + Plate Method */}
      <div className="bg-card border border-border rounded-lg p-6 md:p-8" data-testid="hand-plate-method">
        <div className="flex items-center gap-3">
          <Utensils className="text-primary" size={22} strokeWidth={2.25} />
          <div className="label-eyebrow">The Hand + Plate Method</div>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {HAND_PLATE.map((h) => (
            <div key={h.label} className="rounded-md border border-border p-4" data-testid={`hand-${h.label.toLowerCase().replace(/[^a-z]/g, "")}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{h.label}</div>
              <div className="mt-1 font-head uppercase text-sm">{h.portion}</div>
              <p className="mt-2 text-xs text-foreground/70 leading-relaxed">{h.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-md bg-primary/10 text-foreground px-4 py-3 text-sm">
          <span className="font-head uppercase text-xs tracking-widest text-primary mr-2">Formula:</span>
          1 protein + 1 smart carb + 2 colours of vegetables / fruit
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Portion needs vary. Use as a starting guide, not a medical prescription.</p>
      </div>

      {/* Quick Meal Finder */}
      <div>
        <div className="label-eyebrow">Quick Meal Finder</div>
        <h2 className="wordmark mt-2 text-3xl uppercase">What am I eating today?</h2>
        <div className="mt-5 grid grid-cols-3 gap-3">
          {QUICK.map((q) => (
            <Link key={q.id} to={q.to} className="group bg-foreground text-background rounded-lg p-5 hover:bg-primary transition-colors" data-testid={`quick-${q.id.toLowerCase()}`}>
              <q.icon size={22} strokeWidth={2.25} className="text-primary group-hover:text-primary-foreground" />
              <div className="mt-4 font-head uppercase text-lg">{q.id}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-background/60 group-hover:text-primary-foreground/70">Browse meals</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Choose Your Protein */}
      <div>
        <div className="label-eyebrow">Choose Your Protein</div>
        <h2 className="wordmark mt-2 text-3xl uppercase">Start with what's in the fridge.</h2>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {PROTEIN_CARDS.map((p) => (
            <Link key={p.id} to={p.to} className="group bg-card border border-border rounded-lg p-5 hover:border-primary transition-colors" data-testid={`protein-${p.id.toLowerCase()}`}>
              <div className={`w-10 h-10 rounded-md ${PROTEIN_COLOR[p.id]} text-white inline-flex items-center justify-center`}>
                <p.icon size={20} strokeWidth={2.25} />
              </div>
              <div className="mt-4 font-head uppercase text-xl">{p.id}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{p.count} meals</div>
              <div className="mt-3 text-xs text-primary inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Browse <ArrowRight size={12} /></div>
            </Link>
          ))}
        </div>
      </div>

      {/* Family Favourites */}
      {featured.length > 0 && (
        <div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="label-eyebrow">Family Favourites</div>
              <h2 className="wordmark mt-2 text-3xl uppercase">Easy weeknight wins.</h2>
            </div>
            <Link to="/app/eat/meals" className="text-xs uppercase tracking-widest text-primary inline-flex items-center gap-1 hover:underline" data-testid="see-all-meals">See all 40 <ArrowRight size={12} /></Link>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((m) => (
              <Link key={m.id} to={`/app/eat/meals/${m.slug}`} className="group bg-card border border-border rounded-lg overflow-hidden hover:border-primary transition-colors" data-testid={`featured-${m.slug}`}>
                <div className="relative aspect-[4/5] bg-muted overflow-hidden">
                  {m.image && <img src={m.image} alt={m.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  <div className="absolute top-3 left-3">
                    <span className={`${PROTEIN_COLOR[m.primary_protein] || "bg-foreground"} text-white text-[10px] font-head uppercase tracking-widest px-2 py-1 rounded-md`}>{m.primary_protein}</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.meal_occasion}</div>
                  <div className="mt-1 font-head uppercase text-base leading-tight">{m.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Explore shortcuts */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/app/eat/produce" className="bg-card border border-border rounded-lg p-5 hover:border-primary transition-colors" data-testid="shortcut-produce">
          <Apple className="text-primary" size={22} strokeWidth={2.25} />
          <div className="mt-4 font-head uppercase text-lg">Fruit + Veg</div>
          <div className="mt-1 text-sm text-foreground/70">Colour rotation + 10 yoghurt combos.</div>
        </Link>
        <Link to="/app/eat/shopping" className="bg-card border border-border rounded-lg p-5 hover:border-primary transition-colors" data-testid="shortcut-shopping">
          <ShoppingBasket className="text-primary" size={22} strokeWidth={2.25} />
          <div className="mt-4 font-head uppercase text-lg">Shopping Base</div>
          <div className="mt-1 text-sm text-foreground/70">The 10K30 family trolley checklist.</div>
        </Link>
        <Link to="/app/eat/prep" className="bg-card border border-border rounded-lg p-5 hover:border-primary transition-colors" data-testid="shortcut-prep">
          <ChefHat className="text-primary" size={22} strokeWidth={2.25} />
          <div className="mt-4 font-head uppercase text-lg">Meal Prep</div>
          <div className="mt-1 text-sm text-foreground/70">Cook once. Eat twice. Move on.</div>
        </Link>
        <Link to="/app/eat/principles" className="bg-card border border-border rounded-lg p-5 hover:border-primary transition-colors" data-testid="shortcut-principles">
          <ClipboardList className="text-primary" size={22} strokeWidth={2.25} />
          <div className="mt-4 font-head uppercase text-lg">Principles</div>
          <div className="mt-1 text-sm text-foreground/70">Eight simple rules. No macro counting.</div>
        </Link>
      </div>

      {/* Favourites */}
      {favCount > 0 && (
        <Link to="/app/eat/meals?tag=&favourites=1" className="block bg-primary text-primary-foreground rounded-lg p-5 hover:opacity-95 transition-opacity" data-testid="favourites-shortcut">
          <div className="flex items-center gap-3">
            <Heart size={20} strokeWidth={2.5} fill="currentColor" />
            <div>
              <div className="label-eyebrow !text-primary-foreground/80">Your Favourites</div>
              <div className="font-head uppercase text-lg mt-1">{favCount} saved meal{favCount === 1 ? "" : "s"}</div>
            </div>
          </div>
        </Link>
      )}

      {/* Today's Plate — logger (preserved) */}
      <div className="bg-card border border-border rounded-lg p-6 md:p-8" data-testid="plate-logger">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow">Today's Plate · {today()}</div>
            <h2 className="wordmark mt-2 text-3xl uppercase">Log what you ate.</h2>
            <p className="text-sm text-muted-foreground mt-1">Tick each portion once you've had it today.</p>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Score</div>
            <div className="wordmark text-6xl text-primary leading-none" data-testid="plate-score">{total}<span className="text-foreground/40">/3</span></div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{total === 3 ? "Full plate" : total === 0 ? "Empty" : "Partial"}</div>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-3">
          {PORTIONS.map((p) => {
            const on = portions[p.key];
            return (
              <button key={p.key} onClick={() => toggle(p.key)} disabled={saving}
                className={`text-left rounded-md p-5 border transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}
                data-testid={`portion-${p.key}`} aria-pressed={on}>
                <div className="flex items-center justify-between">
                  <p.icon className={on ? "text-primary" : "text-foreground/60"} size={26} strokeWidth={2.25} />
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${on ? "bg-primary border-primary text-primary-foreground" : "border-foreground/40"}`}>
                    {on && <Check size={14} strokeWidth={3} />}
                  </div>
                </div>
                <div className={`mt-4 font-head uppercase text-lg ${on ? "" : "text-foreground"}`}>{p.label}</div>
                <div className={`mt-1 text-xs ${on ? "text-background/70" : "text-muted-foreground"}`}>{p.sub}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {saving ? "Saving…" : dirty ? `Unsaved · ${savedTotal}/3 saved` : savedTotal === 0 ? "Nothing logged for today yet." : `Logged for today: ${savedTotal}/3`}
          </div>
          <div className="flex gap-2">
            <button onClick={reset} disabled={total === 0 || saving} className="btn-secondary btn-sm disabled:opacity-40" data-testid="plate-reset">Clear</button>
            <button onClick={logPlate} disabled={!dirty || saving} className="btn-primary btn-sm disabled:opacity-40" data-testid="plate-log">{saving ? "Logging…" : `Log ${total}/3`}</button>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">Consistency beats perfect meals.</p>
    </div>
  );
}
